import { BrowserProvider, Contract } from "ethers";
import { ensureGasLeft, tradeErrorMessage } from "@/lib/poolSwap";

const SLIPPAGE_BPS = 100n; // 1% for liquidity moves
const USDT_ABI = [
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function balanceOf(address) view returns (uint256)",
];
let lpAbi;
const loadAbi = async () => (lpAbi = lpAbi || (await (await fetch("/contracts/FSCLiquidityPool.json")).json()).abi);

/** USDT (raw) needed alongside `fmiWei` at the pool's current ratio, rounded up like the contract. */
export const usdtForFmi = (fmiWei, pool) => {
  const rf = BigInt(pool.reservesRaw.FMI);
  const ru = BigInt(pool.reservesRaw.USDT);
  return rf === 0n ? 0n : (fmiWei * ru + rf - 1n) / rf;
};

/** Deposits FMI + matching USDT; approves USDT first if needed. */
export async function addLiquidity({ eip1193, pool, usdtAddress, fmiWei, onStep }) {
  const abi = await loadAbi();
  const provider = new BrowserProvider(eip1193);
  const signer = await provider.getSigner();
  const owner = await signer.getAddress();
  const usdtNeeded = usdtForFmi(fmiWei, pool);
  const usdt = new Contract(usdtAddress, USDT_ABI, signer);
  if ((await usdt.balanceOf(owner)) < usdtNeeded) throw new Error("Insufficient USDT balance");
  if ((await usdt.allowance(owner, pool.address)) < usdtNeeded) {
    onStep("Approve USDT for the pool in your wallet…");
    await (await usdt.approve(pool.address, usdtNeeded)).wait();
  }
  const lp = new Contract(pool.address, abi, signer);
  const supply = BigInt(pool.totalSharesRaw);
  const expected = (fmiWei * supply) / BigInt(pool.reservesRaw.FMI);
  const minShares = (expected * (10000n - SLIPPAGE_BPS)) / 10000n;
  await ensureGasLeft(provider, owner, fmiWei, () => lp.addLiquidity.estimateGas(usdtNeeded, minShares, { value: fmiWei }));
  onStep("Confirm the deposit in your wallet…");
  const tx = await lp.addLiquidity(usdtNeeded, minShares, { value: fmiWei });
  onStep("Adding liquidity on FSC…");
  await tx.wait();
  return tx.hash;
}

/** Withdraws `sharesRaw` LP shares for the matching FMI + USDT. */
export async function removeLiquidity({ eip1193, pool, sharesRaw, onStep }) {
  const abi = await loadAbi();
  const signer = await new BrowserProvider(eip1193).getSigner();
  const lp = new Contract(pool.address, abi, signer);
  const supply = BigInt(pool.totalSharesRaw);
  const minFmi = (sharesRaw * BigInt(pool.reservesRaw.FMI) * (10000n - SLIPPAGE_BPS)) / supply / 10000n;
  const minUsdt = (sharesRaw * BigInt(pool.reservesRaw.USDT) * (10000n - SLIPPAGE_BPS)) / supply / 10000n;
  onStep("Confirm the withdrawal in your wallet…");
  const tx = await lp.removeLiquidity(sharesRaw, minFmi, minUsdt);
  onStep("Withdrawing liquidity on FSC…");
  await tx.wait();
  return tx.hash;
}

export const liquidityErrorMessage = (err) => tradeErrorMessage(err, "Transaction", "the deposit and gas");
